// src/pages/Projects.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Modal,
  Form,
  Alert,
} from "react-bootstrap";
import {
  getProjects,
  createProject,
  updateProject,
  deleteProject,
  assignStudentToProject,
  removeStudentFromProject,
  PROJECT_STATUS,
  PROJECT_PRIORITY,
  getProjectStatusConfig,
  getProjectPriorityConfig,
  getProjectStats,
} from "../services/projectService";
import { getStudents } from "../services/userService";
import { getTasksByProjectId, getAssignedTasksByProjectId, TASK_PRIORITY } from "../services/taskService";
import { logStudentActivity } from "../services/activityService";
import { useAuth } from "../context/AuthProvider";
import Loader from "../components/Loader";
import { Pen, PencilIcon, TrashIcon, EyeIcon } from "lucide-react";

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectTasks, setProjectTasks] = useState([]);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [projectStats, setProjectStats] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: PROJECT_STATUS.PLANNING,
    priority: PROJECT_PRIORITY.MEDIUM,
    startDate: "",
    endDate: "",
    budget: "",
    assignedStudents: [],
  });

  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    search: "",
  });

  useEffect(() => {
    fetchData();
    if (user?.role === 'admin') {
      fetchTutors();
    }
  }, []);

  const fetchTutors = async () => {
    const { getTutors } = await import('../services/userService');
    const data = await getTutors();
    setTutors(data);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projectsData, studentsData, statsData] = await Promise.all([
        getProjects(),
        getStudents(),
        getProjectStats(),
      ]);

      setProjects(projectsData);
      setStudents(studentsData);
      setProjectStats(statsData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.description.trim()) {
      alert("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      let activityMessage;
      if (editingProject) {
        await updateProject(editingProject.id, formData);
        activityMessage = `${user.name} updated project: ${formData.name}`;
      } else {
        await createProject(formData);
      }

      // Log activity
      const activity = {
        userId: user.uid,
        action: editingProject ? "update_project" : "create_project",
        entityType: "project",
        entityId: editingProject?.id || null,
        description: activityMessage,
        metadata: {
          projectName: formData.name,
          status: formData.status,
          priority: formData.priority,
        },
      };

      logStudentActivity(activity).catch((err) => console.error(err));

      await fetchData();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving project:", error);
    }
    setSubmitting(false);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description,
      status: project.status,
      priority: project.priority,
      startDate: formatDateForInput(project.startDate),
      endDate: formatDateForInput(project.endDate),
      budget: project.budget || "",
      assignedStudents: project.assignedStudents || [],
    });
    setShowModal(true);
  };

  const handleDelete = async (projectId) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        // Get project name before deletion for activity log
        const project = projects.find((p) => p.id === projectId);
        const projectName = project?.name || "Unknown project";

        await deleteProject(projectId);

        // Log activity
        const activity = {
          userId: user.uid,
          action: "delete_project",
          entityType: "project",
          entityId: projectId,
          description: `${user.name} deleted project: ${projectName}`,
          metadata: {
            projectName,
          },
        };

        logStudentActivity(activity).catch((err) => console.error(err));

        await fetchData();
      } catch (error) {
        console.error("Error deleting project:", error);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProject(null);
    setFormData({
      name: "",
      description: "",
      status: PROJECT_STATUS.PLANNING,
      priority: PROJECT_PRIORITY.MEDIUM,
      startDate: "",
      endDate: "",
      budget: "",
      assignedStudents: [],
    });
  };

  const handleStudentAssignment = (project) => {
    setSelectedProject(project);
    setShowStudentModal(true);
  };

  const handleViewTasks = async (project) => {
    setSelectedProject(project);
    setTasksLoading(true);
    setShowTasksModal(true);
    
    try {
      const [tasks, assigned] = await Promise.all([
        getTasksByProjectId(project.id),
        getAssignedTasksByProjectId(project.id)
      ]);
      
      setProjectTasks(tasks);
      setAssignedTasks(assigned);
    } catch (error) {
      console.error("Error fetching project tasks:", error);
      setProjectTasks([]);
      setAssignedTasks([]);
    }
    
    setTasksLoading(false);
  };

  const handleAssignStudent = async (studentId) => {
    try {
      await assignStudentToProject(selectedProject.id, studentId);

      // Get student name for activity log
      const student = students.find((s) => s.id === studentId);
      const studentName = student
        ? `${student.firstName} ${student.lastName}`
        : "Unknown student";

      // Log activity
      const activity = {
        userId: user.uid,
        action: "assign_student_to_project",
        entityType: "project",
        entityId: selectedProject.id,
        description: `${user.name} assigned a project: ${selectedProject.name}`,
        metadata: {
          projectName: selectedProject.name,
          studentId,
          studentName,
        },
      };

      logStudentActivity(activity).catch((err) => console.error(err));

      await fetchData();
    } catch (error) {
      console.error("Error assigning student:", error);
    }
  };

  const handleRemoveStudent = async (studentId) => {
    try {
      // Get student name for activity log before removal
      const student = students.find((s) => s.id === studentId);
      const studentName = student
        ? `${student.firstName} ${student.lastName}`
        : "Unknown student";

      await removeStudentFromProject(selectedProject.id, studentId);

      // Log activity
      const activity = {
        userId: user.uid,
        action: "remove_student_from_project",
        entityType: "project",
        entityId: selectedProject.id,
        description: `${user.name} removed you from project: ${selectedProject.name}`,
        metadata: {
          projectName: selectedProject.name,
          studentId,
          studentName,
        },
      };

      logStudentActivity(activity).catch((err) => console.error(err));

      await fetchData();
    } catch (error) {
      console.error("Error removing student:", error);
    }
  };

  const getFilteredProjects = () => {
    return projects.filter((project) => {
      const matchesStatus =
        !filters.status || project.status === filters.status;
      const matchesPriority =
        !filters.priority || project.priority === filters.priority;
      const matchesSearch =
        !filters.search ||
        project.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        project.description
          .toLowerCase()
          .includes(filters.search.toLowerCase());

      return matchesStatus && matchesPriority && matchesSearch;
    });
  };

  const formatDate = (date) => {
    if (!date || date === "" || date === null || date === undefined) {
      return "Not set";
    }

    // Handle different date formats
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }

    // If it's a timestamp number
    if (typeof date === "number") {
      return new Date(date).toLocaleDateString();
    }

    // If it has a toDate method (Firestore timestamp)
    if (date && typeof date.toDate === "function") {
      return date.toDate().toLocaleDateString();
    }

    return "Not set";
  };

  // Helper function to format date for input fields
  const formatDateForInput = (date) => {
    if (!date) return "";

    let jsDate;

    // Handle different date formats
    if (date instanceof Date) {
      jsDate = date;
    } else if (typeof date === "number") {
      jsDate = new Date(date);
    } else if (date && typeof date.toDate === "function") {
      jsDate = date.toDate();
    } else {
      return "";
    }

    // Convert to YYYY-MM-DD format for input[type="date"]
    return jsDate.toISOString().split("T")[0];
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Container fluid>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">🚀 Project Management</h2>
            <Button variant="primary" onClick={() => setShowModal(true)}>
              Add New Project
            </Button>
          </div>

          {/* Project Statistics */}
          {projectStats && (
            <Row className="mb-4">
              <Col md={2}>
                <Card className="text-center border-primary">
                  <Card.Body>
                    <h4 className="text-primary">{projectStats.total}</h4>
                    <small className="text-muted">Total Projects</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center border-secondary">
                  <Card.Body>
                    <h4 className="text-secondary">{projectStats.planning}</h4>
                    <small className="text-muted">Planning</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center border-primary">
                  <Card.Body>
                    <h4 className="text-primary">{projectStats.inProgress}</h4>
                    <small className="text-muted">In Progress</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center border-warning">
                  <Card.Body>
                    <h4 className="text-warning">{projectStats.review}</h4>
                    <small className="text-muted">Review</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center border-success">
                  <Card.Body>
                    <h4 className="text-success">{projectStats.completed}</h4>
                    <small className="text-muted">Completed</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="text-center border-info">
                  <Card.Body>
                    <h4 className="text-info">{projectStats.onHold}</h4>
                    <small className="text-muted">On Hold</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Filters */}
          <Card className="mb-4">
            <Card.Body>
              <Row>
                <Col md={3}>
                  <Form.Control
                    type="text"
                    placeholder="Search projects..."
                    value={filters.search}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        search: e.target.value,
                      }))
                    }
                  />
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                  >
                    <option value="">All Status</option>
                    <option value={PROJECT_STATUS.PLANNING}>Planning</option>
                    <option value={PROJECT_STATUS.IN_PROGRESS}>
                      In Progress
                    </option>
                    <option value={PROJECT_STATUS.REVIEW}>Review</option>
                    <option value={PROJECT_STATUS.COMPLETED}>Completed</option>
                    <option value={PROJECT_STATUS.ON_HOLD}>On Hold</option>
                    <option value={PROJECT_STATUS.CANCELLED}>Cancelled</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={filters.priority}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        priority: e.target.value,
                      }))
                    }
                  >
                    <option value="">All Priority</option>
                    <option value={PROJECT_PRIORITY.LOW}>Low</option>
                    <option value={PROJECT_PRIORITY.MEDIUM}>Medium</option>
                    <option value={PROJECT_PRIORITY.HIGH}>High</option>
                    <option value={PROJECT_PRIORITY.CRITICAL}>Critical</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Button
                    variant="outline-secondary"
                    onClick={() =>
                      setFilters({ status: "", priority: "", search: "" })
                    }
                  >
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Projects Table */}
          <Card>
            <Card.Body>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead className="table-dark">
                    <tr>
                      <th>#</th>
                      <th>Project Name</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Students</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Progress</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredProjects().length === 0 ? (
                      <tr>
                        <td colSpan="9" className="text-center text-muted">
                          No projects found
                        </td>
                      </tr>
                    ) : (
                      getFilteredProjects().map((project, index) => {
                        const statusConfig = getProjectStatusConfig(
                          project.status
                        );
                        const priorityConfig = getProjectPriorityConfig(
                          project.priority
                        );

                        return (
                          <tr key={project.id}>
                            <td>{index + 1}</td>
                            <td>
                              <div>
                                <strong>{project.name}</strong>
                                <br />
                                <small className="text-muted">
                                  {project.description.substring(0, 50)}...
                                </small>
                              </div>
                            </td>
                            <td>
                              <Badge bg={statusConfig.bg}>
                                {statusConfig.icon} {statusConfig.text}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={priorityConfig.bg}>
                                {priorityConfig.icon} {priorityConfig.text}
                              </Badge>
                            </td>
                            <td>
                              <div className="flex flex-col gap-2">
                              <Badge bg="info" className="me-1">
                                {project.assignedStudents?.length || 0} students
                              </Badge>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleStudentAssignment(project)}
                              >
                                Manage
                              </Button>
                              </div>
                            </td>
                            <td>{formatDate(project.startDate)}</td>
                            <td>{formatDate(project.endDate)}</td>
                            <td>
                              <div
                                className="progress"
                                style={{ height: "20px" }}
                              >
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{ width: `${project.progress || 0}%` }}
                                  aria-valuenow={project.progress || 0}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                >
                                  {project.progress || 0}%
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  size="sm"
                                  variant="info"
                                  onClick={() => handleViewTasks(project)}
                                  title="View Tasks"
                                >
                                  <EyeIcon size={16} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={() => handleEdit(project)}
                                  title="Edit Project"
                                >
                                  <PencilIcon size={16} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => handleDelete(project.id)}
                                  title="Delete Project"
                                >
                                  <TrashIcon size={16} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Project Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingProject ? "✏️ Edit Project" : "➕ Add New Project"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Project Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter project name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Budget</Form.Label>
                  <Form.Control
                    type="number"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    placeholder="Project budget"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                Description <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter project description"
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value={PROJECT_STATUS.PLANNING}>📋 Planning</option>
                    <option value={PROJECT_STATUS.IN_PROGRESS}>
                      🚀 In Progress
                    </option>
                    <option value={PROJECT_STATUS.REVIEW}>👀 Review</option>
                    <option value={PROJECT_STATUS.COMPLETED}>
                      ✅ Completed
                    </option>
                    <option value={PROJECT_STATUS.ON_HOLD}>⏸️ On Hold</option>
                    <option value={PROJECT_STATUS.CANCELLED}>
                      ❌ Cancelled
                    </option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                  >
                    <option value={PROJECT_PRIORITY.LOW}>🟢 Low</option>
                    <option value={PROJECT_PRIORITY.MEDIUM}>🟡 Medium</option>
                    <option value={PROJECT_PRIORITY.HIGH}>🔴 High</option>
                    <option value={PROJECT_PRIORITY.CRITICAL}>
                      🚨 Critical
                    </option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    min={formData.startDate}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseModal}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {editingProject ? "Updating..." : "Creating..."}
              </>
            ) : editingProject ? (
              "Update Project"
            ) : (
              "Create Project"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Student Assignment Modal */}
      <Modal
        show={showStudentModal}
        onHide={() => setShowStudentModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            👥 Manage Members - {selectedProject?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProject && (
            <div>
              {/* Assigned Members */}
              <div className="mb-4">
                <h6>
                  Assigned Members (
                  {selectedProject.assignedStudents?.length || 0})
                </h6>
                {selectedProject.assignedStudents?.length === 0 ? (
                  <Alert variant="info">
                    No members assigned to this project.
                  </Alert>
                ) : (
                  <div className="border rounded p-2">
                    {selectedProject.assignedStudents?.map((memberId) => {
                      const student = students.find((s) => s.id === memberId);
                      const tutor = tutors.find((t) => t.id === memberId);
                      if (student) {
                        return (
                          <div
                            key={memberId}
                            className="d-flex justify-content-between align-items-center border-bottom py-2"
                          >
                            <div>
                              <strong>{student.name}</strong> <Badge bg="info">Student</Badge>
                              <br />
                              <small className="text-muted">{student.email}</small>
                            </div>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleRemoveStudent(memberId)}
                            >
                              Remove
                            </Button>
                          </div>
                        );
                      } else if (tutor) {
                        return (
                          <div
                            key={memberId}
                            className="d-flex justify-content-between align-items-center border-bottom py-2"
                          >
                            <div>
                              <strong>{tutor.name}</strong> <Badge bg="warning">Tutor</Badge>
                              <br />
                              <small className="text-muted">{tutor.email}</small>
                            </div>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleRemoveStudent(memberId)}
                            >
                              Remove
                            </Button>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                )}
              </div>

              {/* Available Students/Tutors */}
              <div>
                <h6>Available Members</h6>
                <div
                  className="border rounded p-2"
                  style={{ maxHeight: "300px", overflowY: "auto" }}
                >
                  {/* Tutors (admin only) */}
                  {user?.role === 'admin' && tutors
                    .filter((tutor) => !selectedProject.assignedStudents?.includes(tutor.id))
                    .map((tutor) => (
                      <div
                        key={tutor.id}
                        className="d-flex justify-content-between align-items-center border-bottom py-2"
                      >
                        <div>
                          <strong>{tutor.name}</strong> <Badge bg="warning">Tutor</Badge>
                          <br />
                          <small className="text-muted">{tutor.email}</small>
                        </div>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleAssignStudent(tutor.id)}
                        >
                          Assign
                        </Button>
                      </div>
                    ))}
                  {/* Students */}
                  {students
                    .filter((student) => !selectedProject.assignedStudents?.includes(student.id))
                    .map((student) => (
                      <div
                        key={student.id}
                        className="d-flex justify-content-between align-items-center border-bottom py-2"
                      >
                        <div>
                          <strong>{student.name}</strong> <Badge bg="info">Student</Badge>
                          <br />
                          <small className="text-muted">{student.email}</small>
                          {student.studentLevel && (
                            <Badge bg="info" className="ms-2">
                              {student.studentLevel}
                            </Badge>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleAssignStudent(student.id)}
                        >
                          Assign
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowStudentModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Project Tasks View Modal */}
      <Modal
        show={showTasksModal}
        onHide={() => setShowTasksModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            📋 Tasks for Project: {selectedProject?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {tasksLoading ? (
            <div className="text-center py-4">
              <Loader />
            </div>
          ) : (
            <div>
              {/* Project Tasks (Created but not assigned) */}
              <div className="mb-4">
                <h5>📝 Created Tasks ({projectTasks.length})</h5>
                {projectTasks.length === 0 ? (
                  <Alert variant="info">
                    No tasks created for this project yet.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Task Name</th>
                          <th>Description</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Created Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projectTasks.map((task, index) => (
                          <tr key={task.id}>
                            <td>{index + 1}</td>
                            <td>
                              <strong>{task.name}</strong>
                            </td>
                            <td>
                              <small>{task.description.substring(0, 50)}...</small>
                            </td>
                            <td>
                              <Badge
                                bg={
                                  task.priority === TASK_PRIORITY.CRITICAL
                                    ? "danger"
                                    : task.priority === TASK_PRIORITY.HIGH
                                    ? "warning"
                                    : task.priority === TASK_PRIORITY.MEDIUM
                                    ? "info"
                                    : "success"
                                }
                              >
                                {task.priority === TASK_PRIORITY.CRITICAL && "🔴 Critical"}
                                {task.priority === TASK_PRIORITY.HIGH && "🟠 High"}
                                {task.priority === TASK_PRIORITY.MEDIUM && "🟡 Medium"}
                                {task.priority === TASK_PRIORITY.LOW && "🟢 Low"}
                                {!task.priority && "🟡 Medium"}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg="secondary">
                                {task.status?.replace("_", " ").toUpperCase() || "NOT STARTED"}
                              </Badge>
                            </td>
                            <td>
                              <small>
                                {task.createdAt
                                  ? new Date(task.createdAt).toLocaleDateString()
                                  : "N/A"}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Assigned Tasks */}
              <div>
                <h5>👥 Assigned Tasks ({assignedTasks.length})</h5>
                {assignedTasks.length === 0 ? (
                  <Alert variant="warning">
                    No tasks assigned to students for this project yet.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Task Name</th>
                          <th>Assigned To</th>
                          <th>Assignee</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Due Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignedTasks.map((task, index) => (
                          <tr key={task.id}>
                            <td>{index + 1}</td>
                            <td>
                              <strong>{task.taskName}</strong>
                            </td>
                            <td>
                              <Badge bg="primary">{task.userName}</Badge>
                            </td>
                            <td>
                              <small className="text-muted">{task.assigneeName}</small>
                            </td>
                            <td>
                              <Badge
                                bg={
                                  task.priority === TASK_PRIORITY.CRITICAL
                                    ? "danger"
                                    : task.priority === TASK_PRIORITY.HIGH
                                    ? "warning"
                                    : task.priority === TASK_PRIORITY.MEDIUM
                                    ? "info"
                                    : "success"
                                }
                              >
                                {task.priority === TASK_PRIORITY.CRITICAL && "🔴 Critical"}
                                {task.priority === TASK_PRIORITY.HIGH && "🟠 High"}
                                {task.priority === TASK_PRIORITY.MEDIUM && "🟡 Medium"}
                                {task.priority === TASK_PRIORITY.LOW && "🟢 Low"}
                                {!task.priority && "🟡 Medium"}
                              </Badge>
                            </td>
                            <td>
                              <Badge
                                bg={
                                  task.status === "completed"
                                    ? "success"
                                    : task.status === "in_progress"
                                    ? "warning"
                                    : "secondary"
                                }
                              >
                                {task.status?.replace("_", " ").toUpperCase() || "NOT STARTED"}
                              </Badge>
                            </td>
                            <td>
                              <small>
                                {task.dueDate
                                  ? new Date(task.dueDate).toLocaleDateString()
                                  : "No due date"}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Tasks Summary */}
              <Row className="mt-4">
                <Col md={6}>
                  <Card className="text-center border-info">
                    <Card.Body>
                      <h4 className="text-info">{projectTasks.length}</h4>
                      <small className="text-muted">Created Tasks</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="text-center border-success">
                    <Card.Body>
                      <h4 className="text-success">{assignedTasks.length}</h4>
                      <small className="text-muted">Assigned Tasks</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowTasksModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
